import axios from "axios";

// const EXTERNAL_API_URL = "https://www.25thmpc.cloud/api/external/candidates";
const API_KEY =
  "cmr_74e6fff1e6db53ed694772298aeb7712bb0f1b7399a3a00d762d92c5d55532c7";
// const API_KEY = "cmr_d5b319251afa1268257e904c17bc694848eaad23352f473ab6533f7eded3e416";

// Response Types
export interface Candidate {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  professionalTitle: string | null;
  yearsOfExperience: number | null;

  // Scoring & Ratings (1.0-5.0)
  overallRating: number | null;
  interviewRating: number | null;
  cvJobMatchRating: number | null;
  manualHrRating: number | null;
  experienceRating: number | null;
  educationRating: number | null;
  certificationsRating: number | null;

  // Location & Salary
  location: {
    city: string | null;
    state: string | null;
    country: string | null;
  };
  expectedSalaryRange: {
    min: number | null;
    max: number | null;
    currency: string;
  } | null;

  // Skills, Education, Certifications
  skills: Array<{
    skillName: string;
    level: string;
    yearsOfExperience: number | null;
  }>;
  certifications: Array<{ name: string; issuingOrganization: string }>;
  highestEducation: {
    level: string;
    degree: string | null;
    institution: string;
  } | null;
  gender?: string; // Added to support gender filtering based on prompt
}

export type ExternalCandidate = Candidate;

export interface CandidatesResponse {
  success: boolean;
  data: Candidate[];
  pagination: {
    page: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
  };
}

export interface ExternalPagination {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface ExternalApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
  pagination?: ExternalPagination;
}

const externalAxios = axios.create({
  baseURL: "/api/external",
  headers: {
    Accept: "application/json",
    Authorization: `Bearer ${API_KEY}`,
  },
});

// Candidate Filters Interface
export interface CandidateFilters {
  query?: string;
  skills?: string[];
  isActive?: boolean;
  country?: string;
  state?: string;
  gender?: string;
  interviewRating?: number;
  cvJobMatchRating?: number;
  overallRating?: number; // Mapping "25th Rating" to overall? Or manualHr?
}

export const externalCandidateService = {
  /**
   * Fetch a list of candidates from the external API with filtering.
   */
  fetchCandidates: async (
    page: number = 1,
    pageSize: number = 20,
    filters?: CandidateFilters,
  ): Promise<CandidatesResponse> => {
    const params: any = {
      page: String(page),
      pageSize: String(pageSize),
    };

    if (filters?.query) params.query = filters.query;
    if (filters?.skills?.length) params.skills = filters.skills.join(",");
    if (filters?.isActive !== undefined)
      params.isActive = String(filters.isActive);

    // Add additional filters requested
    if (filters?.country) params.country = filters.country;
    if (filters?.state) params.state = filters.state;
    if (filters?.gender && filters.gender !== "All")
      params.gender = filters.gender;

    // Rating filters
    // Note: Verify if API supports exact match or min/max. Assuming min or exact.
    if (filters?.interviewRating)
      params.interviewRating = String(filters.interviewRating);
    if (filters?.cvJobMatchRating)
      params.cvJobMatchRating = String(filters.cvJobMatchRating);
    if (filters?.overallRating)
      params.overallRating = String(filters.overallRating);

    try {
      const response = await externalAxios.get<CandidatesResponse>(
        "/candidates",
        { params },
      );
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error) && error.response) {
        throw new Error(
          `API Error: ${error.response.data?.error || error.message}`,
        );
      }
      throw error;
    }
  },

  /**
   * Create a new candidate record.
   * Keeping this for backward compatibility if needed, though types validation might fail
   * if ExternalCandidate was used elsewhere.
   * Ideally, we should unify the types.
   */
  // create: async (candidate: any) => {
  //   const response = await externalAxios.post(
  //     "/api/external/candidates",
  //     candidate
  //   );
  //   return response.data;
  // },
};
